import { useTranslation } from 'react-i18next';

import { Font, View } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { ChartPdfImagesGroup } from 'PDF/components/Charts/ChartPdfImagesGroup';
import { emptyChart } from 'PDF/components/Charts/types';
import { InfoTable } from 'PDF/components/InfoTable';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { ProductsServicesDescription } from 'PDF/components/ProductsServicesDescription';
import { SmallHeading } from 'PDF/components/SmallHeading';
import { StripedTableWrapper } from 'PDF/components/StripedTable';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { VerticalTimelineImagePdf } from 'PDF/components/Timeline/VerticalTimelineImagePdf';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import {
  BusinessOperationsKeys,
  BusinessOperationsSectionAvailability,
  getBusinessOperationsChapterNames,
} from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { DELIVERY_TERMS_OPTIONS } from 'components/BusinessToolForm/constants/options';

import { IBusinessOperationsPdfData } from './types';
import { SectionCoverPagePdf } from '../SectionCoverPage';
import { getFinalSubChapterNames, getIsBuildingsPremisesDetailsEmpty, getIsEquipmentGroupsEmpty } from './mapping';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface IBusinessOperationsPdfProps {
  magazineItems: ITextItem[][];
  data: IBusinessOperationsPdfData;
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const BusinessOperationsPdf = (
  props: IBusinessOperationsPdfProps & {
    availability: BusinessOperationsSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage
  } = props;
  const { t } = useTranslation();
  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.BUSINESS_OPERATIONS, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const BUSINESS_OPERATIONS_CHAPTER_NAMES = getBusinessOperationsChapterNames(isExamplePage);
  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const finalSubChapterNames = getFinalSubChapterNames(Object.keys(availability) as BusinessOperationsKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${BUSINESS_OPERATIONS_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.BUSINESS_OPERATIONS)}
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 0, variant: 'before' }}
        subChapterNames={finalSubChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const {
    buildingsPremisesDetails,
    equipmentGroups,
    plannedPurchases,
    assetGroups,
    futureAcquisitions,
    processStagesImg,
    supplierOverviewData,
    processStagesNumber,
    characteristics,
    productionPlanImages,
    certifications,
    patentsOrTrademarks,
  } = data;
  const isSupplierOverviewEmpty =
    supplierOverviewData.length === 0 ||
    (supplierOverviewData.length === 1 &&
      supplierOverviewData[0].length === 1 &&
      supplierOverviewData[0][0].info === DELIVERY_TERMS_OPTIONS[0].value);

  const isBuildingsPremisesDetailsEmpty = getIsBuildingsPremisesDetailsEmpty(buildingsPremisesDetails, isExamplePage);
  const isEquipmentGroupsEmpty = getIsEquipmentGroupsEmpty(equipmentGroups);

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.BUSINESS_OPERATIONS)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 0, variant: 'before' }}
          subChapterNames={finalSubChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.location && (
            <>
              <SubChapterHeading id="location" heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.location)} />
              <PdfContentText
                items={magazineItems[0]}
                withoutBottomMargin={!isInvestor && isBuildingsPremisesDetailsEmpty}
              />
              {!isInvestor && (
                <>
                  <StripedTableWrapper heading={isExamplePage ? "Buildings/Premises Details" :  t('pdfPages.mappings.businessOperations.buildingPremises')} rows={buildingsPremisesDetails} />
                  <PdfContentText items={magazineItems[1]} />
                </>
              )}
            </>
          )}
          {availability.equipment && (
            <>
              <SubChapterHeading id="equipment" heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.equipment)} />
              <PdfContentText
                items={magazineItems[!isInvestor ? 2 : 1]}
                withoutBottomMargin={!isInvestor && isEquipmentGroupsEmpty}
              />
              <InfoTable heading={isExamplePage ? "Equipment Groups" : t('pdfPages.mappings.businessOperations.equipmentGroups')} rows={equipmentGroups} />
              {!isInvestor && (
                <PdfContentText items={magazineItems[3]} withoutBottomMargin={plannedPurchases.length === 0} />
              )}
              <InfoTable heading={isExamplePage ? "Planned Purchases" : t('pdfPages.mappings.businessOperations.plannedPurchases')} rows={plannedPurchases} />
              {!isInvestor && <PdfContentText items={magazineItems[4]} />}
            </>
          )}
          {availability.assets && (
            <>
              <SubChapterHeading id="assets" heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.assets)} />
              <PdfContentText
                items={magazineItems[!isInvestor ? 5 : 2]}
                withoutBottomMargin={assetGroups.length === 0 && !isInvestor}
              />
              <InfoTable heading={isExamplePage ? "Asset Groups" : t('pdfPages.mappings.businessOperations.assetGroups')} rows={assetGroups} />
              <InfoTable heading={isExamplePage ? "Future Acquisitions" : t('pdfPages.mappings.businessOperations.futureAcquisitions')} rows={futureAcquisitions} />
              {!isInvestor && <PdfContentText items={magazineItems[6]} />}
            </>
          )}
          {availability.technologicalProcess && (
            <>
              <SubChapterHeading
                id="technologicalProcess"
                heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.technologicalProcess)}
              />
              <PdfContentText items={magazineItems[!isInvestor ? 7 : 3]} withoutBottomMargin={!processStagesImg} />
              {processStagesImg && (
                <View wrap={false}>
                  <VerticalTimelineImagePdf
                    heading={isExamplePage ? "Process Stages" : t('pdfPages.mappings.businessOperations.processStages')}
                    imageSrc={processStagesImg}
                    itemsNumber={processStagesNumber}
                  />
                </View>
              )}
              <PdfContentText items={magazineItems[!isInvestor ? 8 : 4]} />
              {isInvestor && !isSupplierOverviewEmpty && (
                <InfoTable heading={isExamplePage ? "Supplier Overview" : t('pdfPages.mappings.businessOperations.supplierOverview')} rows={supplierOverviewData} />
              )}
            </>
          )}
          {availability.workingResources && (
            <>
              <SubChapterHeading
                id="workingResources"
                heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.workingResources)}
                minPresenceAhead={0}
              />
              <PdfContentText items={magazineItems[9]} />
            </>
          )}
          {availability.partners && (
            <>
              <SubChapterHeading
                id="partners"
                heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.partners)}
                minPresenceAhead={!isSupplierOverviewEmpty ? 250 : 0}
              />
              {!isSupplierOverviewEmpty && <InfoTable heading={isExamplePage ? "Supplier Overview" : t('pdfPages.mappings.businessOperations.supplierOverview')} rows={supplierOverviewData} />}
              <PdfContentText items={magazineItems[10]} />
            </>
          )}
          {availability.production && (
            <>
              <SubChapterHeading
                id="production"
                heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.production)}
                minPresenceAhead={productionPlanImages.length > 0 ? 400 : 0}
              />
              {productionPlanImages.length > 0 && (
                <>
                  {productionPlanImages.some(({ src }) => src !== emptyChart) && (
                    <SmallHeading heading={isExamplePage ? "Production Plans" : t('pdfPages.mappings.businessOperations.productionPlans')} />
                  )}
                  <ChartPdfImagesGroup images={productionPlanImages} />
                </>
              )}
              <PdfContentText items={magazineItems[!isInvestor ? 11 : 5]} />
            </>
          )}
          {availability.quality && (
            <>
              <SubChapterHeading id="quality" heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.quality)} />
              <PdfContentText items={magazineItems[12]} />
            </>
          )}
          {availability.productDescription && (
            <ProductsServicesDescription
              id="productDescription"
              heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.productDescription)}
              magazineItems={[magazineItems[6], magazineItems[7]]}
              certifications={certifications}
              characteristics={characteristics}
              patentsOrTrademarks={patentsOrTrademarks}
              isExamplePage={isExamplePage}
            />
          )}
          {availability.advantages && (
            <>
              <SubChapterHeading
                id="advantages"
                heading={getSubHeading(BUSINESS_OPERATIONS_CHAPTER_NAMES.advantages)}
              />
              <PdfContentText items={magazineItems[8]} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
